import { ReactComponent as BookmarkIcon } from '@assets/images/app/bookmark.svg';
import { ReactComponent as DefaultAvatar } from '@assets/images/app/default-avatar-gray.svg';
import { ReactComponent as HeartOffGrayIcon } from '@assets/images/app/heart-gray.svg';
import { ReactComponent as ShareIcon } from '@assets/images/app/share.svg';
import { ReactComponent as StarOn } from '@assets/images/app/star-pink.svg';
import { DividerBold } from '@components/common/Divider';
import { Linkify } from '@components/common/Linkify';
import { PRODUCT_ACTIVITY_TYPE_TEXT } from '@models/product';
import { ExtendedUserContent, REVIEW_FACE_VALUES } from '@models/user-contents';
import moment from 'moment';
import React from 'react';
import { cdnImageUrl } from '@utils/image';
import { WEB_URL } from '@variables';
import { VoteCard } from './VoteCard';

export interface UserContentDetailCardProps {
  userContent: ExtendedUserContent | null | undefined;
}

export const UserContentDetailCard: React.FC<UserContentDetailCardProps> = ({ userContent }) => {
  if (!userContent) return null;

  const Rate5Icon = REVIEW_FACE_VALUES[`${userContent?.rates?.rate5 || 5}`]?.Icon;
  const rate5Label = REVIEW_FACE_VALUES[`${userContent?.rates?.rate5 || 5}`]?.label;

  return (
    <div className="ProseMirror" style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
      {userContent?.title && (
        <div
          style={{
            color: 'var(--Neutral-100, #242424)',
            fontFamily: 'Pretendard',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '26px',
            padding: '0 20px',
          }}>
          {userContent?.title}
        </div>
      )}
      <div
        style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderTop: '1px solid #EFF1F4',
          borderBottom: '1px solid #EFF1F4',
        }}>
        <div style={{ flexShrink: 0 }}>
          {userContent?.publisher?.imageUrl ? (
            <img
              src={cdnImageUrl(userContent?.publisher?.imageUrl)}
              alt="avatar"
              style={{ objectFit: 'cover', width: '40px', height: '40px', borderRadius: '20px' }}
            />
          ) : (
            <DefaultAvatar />
          )}
        </div>
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div
            style={{
              color: 'var(--Neutral-90, #424242)',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '20px',
            }}>
            {userContent?.publisher?.nickname || '익명'}
          </div>
          <div
            style={{
              color: 'var(--Neutral-60, #9B9DA0)',
              fontFamily: 'Pretendard',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              lineHeight: '18px',
            }}>
            <div>
              {userContent?.publisher?.message ||
                `${userContent?.publisher?.location} · ${userContent?.publisher?.age}`}
            </div>
            <div>{moment(userContent?.updatedAt || userContent?.createdAt).fromNow()}</div>
          </div>
        </div>
      </div>
      <div>
        {userContent?.board === 'review' && (
          <div style={{ padding: '0 20px', marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
              }}>
              <div
                style={{
                  background: '#F5F7FB',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  flex: 1,
                }}>
                <div
                  style={{
                    color: '#242424',
                    fontFamily: 'Pretendard',
                    fontSize: '40px',
                    fontWeight: 700,
                    lineHeight: '1.2',
                  }}>
                  {userContent.rates
                    ? (
                        (userContent.rates.rate1 +
                          userContent.rates.rate2 +
                          userContent.rates.rate3 +
                          userContent.rates.rate4) /
                        4
                      ).toFixed(1)
                    : '0.0'}
                </div>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarOn key={star} style={{ width: '16px', height: '16px' }} />
                  ))}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  flex: 1,
                }}>
                {[
                  { key: 'rate1', label: '친화력' },
                  { key: 'rate2', label: '준비성' },
                  { key: 'rate3', label: '전문성' },
                  { key: 'rate4', label: '시간준수' },
                ].map((item) => (
                  <div
                    key={item.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      height: '24px',
                    }}>
                    <div
                      style={{
                        color: '#424242',
                        fontFamily: 'Pretendard',
                        fontSize: '12px',
                        fontWeight: 500,
                        width: '50px',
                        textAlign: 'left',
                      }}>
                      {item.label}
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1, 2, 3, 4, 5].map((star) => {
                        const currentRate = userContent.rates?.[item.key as keyof typeof userContent.rates] || 0;
                        return (
                          <div key={star} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {star <= currentRate ? (
                              <StarOn style={{ width: '16px', height: '16px' }} />
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                  d="M8 1L10.18 5.18L14.5 5.82L11.25 9.18L12.36 13.5L8 11.18L3.64 13.5L4.75 9.18L1.5 5.82L5.82 5.18L8 1Z"
                                  fill="#D5D8DD"
                                />
                              </svg>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {userContent.rates?.rate5 !== undefined && userContent.rates?.rate5 > 0 && (
              <div
                style={{
                  background: '#FFF5F9',
                  borderRadius: '8px',
                  padding: '12px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}>
                <Rate5Icon width={20} height={20} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span
                    style={{
                      color: '#FF9EC7',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontWeight: 700,
                    }}>
                    아이 만족도
                  </span>
                  <span
                    style={{
                      color: '#FF3D8F',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontWeight: 700,
                    }}>
                    {`"${rate5Label}"`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        {userContent?.board === 'vote' && (
          <VoteCard
            userContentId={userContent?._id || ''}
            category={userContent?.category}
            title={userContent?.title || ''}
            vote={userContent?.vote || { options: [], startDate: new Date(), endDate: new Date() }}
            voteResult={userContent?.voteResult}
            publishedAt={userContent?.publishedAt}
            styles={{ container: { padding: '0 20px' } }}
          />
        )}
      </div>
      <div className="ProseMirror" style={{ padding: '0 20px', flexShrink: 0 }}>
        <Linkify>{userContent?.text?.replace(/<h1>.*?<\/h1>/g, '')}</Linkify>
      </div>
      {userContent?.product && (
        <button
          className="link-button"
          style={{
            height: '52px',
            display: 'flex',
            gap: '12px',
            margin: '0 20px',
            border: '1px solid #EFF1F4',
            textAlign: 'left',
            alignItems: 'center',
          }}
          onClick={() => {
            window.open(`${WEB_URL}/product/${userContent?.product?._id}`, '_blank');
          }}>
          <img
            src={cdnImageUrl(userContent?.product?.thumbnailImage)}
            alt=""
            style={{
              height: '52px',
              width: '52px',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <div
            style={{
              width: 'calc(100% - 108px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
            <div
              style={{
                wordBreak: 'break-word',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                color: 'var(--Neutral-90, #424242)',
                fontFamily: 'Pretendard',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: 400,
              }}>
              {userContent?.product?.title}
            </div>
            {(userContent?.product?.activityType || userContent?.product?.favoritedCount) && (
              <div
                style={{
                  display: 'flex',
                  gap: '6px',
                  color: 'var(--Neutral-60, #9B9DA0)',
                  textAlign: 'right',
                  fontFamily: 'Pretendard',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  marginTop: '4px',
                }}>
                {userContent?.product?.activityType && (
                  <div>{PRODUCT_ACTIVITY_TYPE_TEXT[userContent?.product?.activityType]?.title}</div>
                )}
                {userContent?.product?.activityType && <div style={{ width: '1px', height: '100%' }}></div>}
                {userContent?.product?.favoritedCount && userContent?.product?.favoritedCount > 0 && (
                  <div>{`찜 ${userContent?.product?.favoritedCount}개`}</div>
                )}
              </div>
            )}
          </div>
        </button>
      )}
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        {userContent?.images &&
          userContent?.images.length > 0 &&
          userContent?.images.map((item, index) => (
            <img key={`image-${index}`} src={cdnImageUrl(item)} alt={`upload-${index}`} style={{ display: 'block' }} />
          ))}
      </div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '0 20px' }}>
        <HeartOffGrayIcon />
        <ShareIcon />
        <BookmarkIcon />
      </div>
      <DividerBold />
    </div>
  );
};
